import { Box, Card, Group, Skeleton, Stack, Text, Tooltip } from '@mantine/core';
import { pickTextColorForBackground } from '@/utils/color';
import type { ActivitySegment } from '../Hooks/useOverviewCharts';

/**
 * ActivityMixBar — one 100% stacked horizontal bar over `CareCaseListView.activityStatus`
 * (Active / Quiet / Stale / Closed).
 *
 * This is the one genuinely *categorical* chart on the Overview: four segments is well
 * inside the safe series count, the categories are an ordered health scale rather than a
 * magnitude ranking, and `activityStatusColor` ships on the DTO — so the lookup colours
 * drive the fill here instead of the sequential ramp the breakdown bars use.
 *
 * Segments are direct-labelled inside the bar when they are wide enough to hold the text,
 * and every segment also appears in the legend below — identity is never colour-alone.
 */
export interface ActivityMixBarProps {
  segments: ActivitySegment[];
  loading?: boolean;
}

/** Below this share a segment is too narrow to hold its own label legibly. */
const INLINE_LABEL_MIN_PERCENT = 12;

/** Floor width for a segment, so a 1% bucket is still clickable and visible. */
const MIN_SEGMENT_PX = 6;

export default function ActivityMixBar({ segments, loading = false }: ActivityMixBarProps) {
  const total = segments.reduce((sum, s) => sum + s.count, 0);

  return (
    <Card withBorder radius="md" padding="lg" h="100%">
      <Text fw={600}>Case activity mix</Text>
      <Text size="sm" c="dimmed" mb="md">
        How much of the caseload is actually moving
      </Text>

      {loading && total === 0 ? (
        <Stack gap="sm">
          <Skeleton height={28} radius="sm" />
          <Skeleton height={16} width="70%" radius="sm" />
        </Stack>
      ) : total === 0 ? (
        <Text size="sm" c="dimmed">
          No cases to classify yet.
        </Text>
      ) : (
        <Stack gap="md">
          <Group gap={2} wrap="nowrap" style={{ width: '100%' }}>
            {segments.map((segment) => {
              const percent = (segment.count / total) * 100;
              return (
                <Tooltip
                  key={segment.status}
                  withArrow
                  label={`${segment.status}: ${segment.count} (${Math.round(percent)}%)`}
                >
                  <Box
                    style={{
                      // A 1% segment must still be a visible sliver, not a hairline.
                      minWidth: MIN_SEGMENT_PX,
                      width: `${percent}%`,
                      height: 30,
                      backgroundColor: segment.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 4,
                      overflow: 'hidden',
                      // Keeps a near-black or near-white activity colour from merging into the
                      // card surface on one theme or the other.
                      boxShadow: 'inset 0 0 0 1px var(--mantine-color-default-border)',
                    }}
                  >
                    {percent >= INLINE_LABEL_MIN_PERCENT && (
                      <Text
                        size="xs"
                        fw={700}
                        style={{ color: pickTextColorForBackground(segment.color) }}
                      >
                        {Math.round(percent)}%
                      </Text>
                    )}
                  </Box>
                </Tooltip>
              );
            })}
          </Group>

          {/* Legend — always present, so the segments are identifiable without colour. */}
          <Group gap="lg" wrap="wrap">
            {segments.map((segment) => (
              <Group key={segment.status} gap={6} wrap="nowrap">
                <Box
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: 3,
                    backgroundColor: segment.color,
                    flexShrink: 0,
                    boxShadow: '0 0 0 1px var(--mantine-color-default-border)',
                  }}
                />
                <Text size="sm">{segment.status}</Text>
                <Text size="sm" fw={700}>
                  {segment.count}
                </Text>
              </Group>
            ))}
          </Group>
        </Stack>
      )}
    </Card>
  );
}
